// Added by Sejune Oh on 2020-06-15
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useState, useEffect } from 'react'
import { createStyles, makeStyles } from '@material-ui/core'

// helper
import Intl from '@/helper/Intl'
import { getDateWithFormat, convDateTimeString_ignoreTZ } from '@/helper/util'
import { TaskMonitoringElement, TaskStatusEnum } from '@/helper/define/taskDefine'
import Logger from '@/helper/Logger'

// api
import { reqCancelTaskById, ReqTaskControlById, reqKillTaskByIdWithProcName } from '@/service/study/task.service'

// ui
import { ConfirmDialog } from '@/components/common/v2/Dialog/ConfirmDialog'
import { Button } from '@/components/worklist/Button'

///////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface TaskMonitoringDetailTableProps {
  tableTitle?: string
  tasks?: TaskMonitoringElement[]
  isRunning?: boolean
  isAdminLevel?: boolean
  proxyToken?: string | null
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const TaskMonitorignDetailTable = ({
  tableTitle,
  tasks = [],
  isRunning = false,
  isAdminLevel = false,
  proxyToken,
  ...rest
}: TaskMonitoringDetailTableProps) => {
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false)
  const [cancelCandiateId, setCancelCandiateId] = useState<string>('')
  const [cancelProcName, setCancelProcName] = useState<string>('')
  const [isCancellable, setIsCancellable] = useState<boolean>(isAdminLevel)
  const [isAbort, setIsAbort] = useState<boolean>(false)
  const classes = useStyles()

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // effect
  useEffect(() => {
    if (!isRunning) return

    if (isAdminLevel) {
      return setIsAbort(true)
    }

    // eslint-disable-next-line
  }, [isRunning, isAdminLevel])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // i18n
  const btnAbort = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.btn_abort' })
  const btnCancel = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.btn_cancel' })
  const colAbort = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_abort' })
  const colCancel = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_cancel' })
  const colInvoker = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_invoker' })
  const colCreateAt = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_created_at' })
  const colStartAt = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_started_at' })
  const colPatientId = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_patient_id' })
  const colPatientName = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_patient_name' })
  const colStudyDate = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_study_date' })
  const colProcessName = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_process_name' })
  const colStudyDesc = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_study_desc' })
  const colRuleName = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.col_rule_name' })
  const titleDialogAbort = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.dialog_title_abort' })
  const titleDialogCancel = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.dialog_title_cancel' })
  const msgConfirmAbort = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.msg_confirm_abort' })
  const msgConfirmCancel = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetailtable.msg_confirm_cancel' })

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // handler
  const handleClickTaskCancelBtn = (id: string, procName: string) => {
    setConfirmDialogOpen(true)
    setCancelCandiateId(id)
    setCancelProcName(procName)
  }

  const handleConfirmDialogCloseBtnClick = () => {
    setConfirmDialogOpen(false)
    setCancelCandiateId('')
  }

  const handleConfirmDialogConfirmBtnClick = async () => {
    if (!proxyToken) return
    try {
      if (!isRunning) {
        const taskInfo: ReqTaskControlById = { proxyToken, taskIds: [cancelCandiateId] }
        await reqCancelTaskById(taskInfo)
      } else {
        const taskInfo = { proxyToken, procName: cancelProcName, taskId: cancelCandiateId }
        await reqKillTaskByIdWithProcName(taskInfo)
      }
    } catch (error) {
      Logger.errLog(`Can't cancel Process because: ${error}`)
    } finally {
      setConfirmDialogOpen(false)
    }
  }

  return (
    <div className={classes.root}>
      <p className={classes.tableTitle}>{tableTitle}</p>
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <colgroup>
            {/* invoker */}
            <col style={{ width: '6%' }} />
            {/* process name */}
            <col style={{ width: '10%' }} />
            {/* rule name */}
            <col style={{ width: '10%' }} />
            {/* created at */}
            <col style={{ width: '10%' }} />
            {/* started at */}
            <col style={{ width: '10%' }} />
            {/* pid */}
            <col style={{ width: '10%' }} />
            {/* pname */}
            <col style={{ width: '10%' }} />
            {/* study date */}
            <col style={{ width: '11%' }} />
            {/* study desc */}
            <col style={{ width: isCancellable ? '15%' : '23%' }} />
            {/* cancel btn only on waiting */}
            {isCancellable && <col style={{ width: '8%' }} />}
          </colgroup>
          <thead>
            <tr>
              <th>{colInvoker}</th>
              <th>{colProcessName}</th>
              <th>{colRuleName}</th>
              <th>{colCreateAt}</th>
              <th>{colStartAt}</th>
              <th>{colPatientId}</th>
              <th>{colPatientName}</th>
              <th>{colStudyDate}</th>
              <th>{colStudyDesc}</th>
              {isCancellable && <th>{isAbort ? colAbort : colCancel}</th>}
            </tr>
          </thead>
          <tbody>
            {tasks.length > 0 &&
              tasks.map(
                ({
                  id,
                  user_display_name,
                  created_at,
                  proc_started_at,
                  patient_id,
                  patient_name,
                  study_date_time,
                  proc_name,
                  study_description,
                  rule_name,
                  status,
                }) => (
                  <tr key={id}>
                    <td>{user_display_name}</td>
                    <td>{proc_name}</td>
                    <td>{rule_name || '-'}</td>
                    <td>{getDateWithFormat(created_at)}</td>
                    <td>{getDateWithFormat(proc_started_at)}</td>
                    <td>{patient_id}</td>
                    <td>{patient_name}</td>
                    <td>{convDateTimeString_ignoreTZ(study_date_time)}</td>
                    <td>{study_description}</td>
                    {isCancellable && (
                      <td>
                        {status !== TaskStatusEnum.Invoking && (
                          <Button type='cancel' onBtnClick={() => handleClickTaskCancelBtn(id, proc_name)}>
                            {isAbort ? btnAbort : btnCancel}
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              )}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        title={isAbort ? titleDialogAbort : titleDialogCancel}
        open={confirmDialogOpen}
        onCloseBtnClick={handleConfirmDialogCloseBtnClick}
        onConfirmBtnClick={handleConfirmDialogConfirmBtnClick}
        onCancelBtnClick={handleConfirmDialogCloseBtnClick}
        messages={isAbort ? msgConfirmAbort : msgConfirmCancel}
        hasCancelBtn
      />
    </div>
  )
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {},
    tableWrapper: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      height: '200px',
      background: '#000',
      border: '1px solid #000',
      overflow: 'hidden auto',
      '&::-webkit-scrollbar-thumb': {
        width: '50px',
        height: '50px',
        borderRadius: '4px',
        background: '#646464',
      },
      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
    },
    tableTitle: {
      fontWeight: 'bold',
      color: '#fff',
    },
    table: {
      border: 0,
      borderCollapse: 'separate',
      borderSpacing: 0,
      '& th': {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: '#383838',
        borderBottom: '1px solid #000',
        paddingLeft: '8px',
        textAlign: 'left',
        '&:before': {
          content: '""',
          position: 'absolute',
          background: '#f00',
          right: 0,
          top: '15%',
          bottom: '15%',
          width: '2px',
          backgroundColor: '#2c2d2f',
        },
        '&:last-of-type:before': {
          content: 'none',
        },
      },
      '& tr': {
        height: '42px',
        backgroundColor: '#1B1B1B',
        boxSizing: 'border-box',
      },
      '& > thead': {
        color: '#e6e6e6',
        top: 0,
        '& > tr': {
          height: '31px',
        },
      },
      '& > tbody': {
        '& > tr': {
          color: '#b4b4b4',
          '&:not(:first-child) > td': {
            borderTop: '1px solid #383838',
          },
        },
        '& td': {
          paddingLeft: '8px',
        },
      },
    },
  })
)

export default TaskMonitorignDetailTable
